import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Save as SaveIcon,
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material'
import { useNavigate, useSearchParams, useParams } from 'react-router-dom'
import { useNotification } from '../contexts/NotificationContext'
import { useSMBContext } from '../contexts/SMBContext'
import campaignApi, { VariableMapping } from '../services/campaignApi'
import { templatesApi } from '../services/api'
// import MediaUpload from '../components/MediaUpload'
import { campaignStorage, CampaignWizardState } from '../utils/campaignStorage'
import { getComponentConstraints, getMetaCreateTemplateUrl } from '../utils/templateUtils'
import TimezoneSelector from '../components/timezone';
import TemplatePreview from '../components/templates/TemplatePreview'
import TemplateSelector from '../components/templates/TemplateSelector'
import SampleCsvSection from '../components/campaign/SampleCsvSection'
import CsvValidationErrorDialog, { CsvErrorFlags } from '../components/campaign/CsvValidationErrorDialog'
import CsvValidationWarningDialog, { CsvWarningFlags } from '../components/campaign/CsvValidationWarningDialog'
import CsvFileClientErrorDialog, { CsvFileClientErrorFlags } from '../components/campaign/CsvFileClientErrorDialog'
import ValidateCsvLoadingDialog from '../components/campaign/ValidateCsvLoadingDialog'
import { MetaTemplate, LocalTemplate, TemplateVariable } from '../types/templates'
import { extractTemplateParameterName, formatTemplateVariableLabel, detectVariablesFromBodyText } from '../utils/templateVariableLabels'
import { filePersistence } from '../utils/filePersistence'
import { getDefaultStartDate, getDefaultEndDate, getRandomStartWindow } from '../utils/dateUtils'
import { validateCsvFile } from '../utils/campaignUtils'

const steps = ['Setup', 'Template', 'Audience', 'Schedule & Launch']


const CampaignWizard: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const { id: campaignId } = useParams<{ id?: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const currentStep = parseInt(searchParams.get('step') || '1', 10)
  const isEditMode = !!campaignId

  const { smbs, selectedSMBId } = useSMBContext()
  const [loading, setLoading] = useState(false)
  const [smbId, setSmbId] = useState<string>('')
  const [templates, setTemplates] = useState<LocalTemplate[]>([])
  const [templateVariables, setTemplateVariables] = useState<TemplateVariable[]>([])
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvPreview, setCsvPreview] = useState<any[]>([])
  const [totalRows, setTotalRows] = useState<number>(0)
  const [csvColumns, setCsvColumns] = useState<string[]>([])
  const [createNewTemplate, setCreateNewTemplate] = useState(false)
  const [existingLeadbook, setExistingLeadbook] = useState<{ filename?: string, uploaded_at?: string } | null>(null)
  const [newTemplateData, setNewTemplateData] = useState({
    name: '',
    language: 'en_US',
    category: 'marketing',
    header_type: 'none',
    media_id: '',
    media_url: '',
    body_text: '',
  })
  const [metaTemplates, setMetaTemplates] = useState<MetaTemplate[]>([])
  const [selectedMetaTemplate, setSelectedMetaTemplate] = useState<MetaTemplate | null>(null)
  const [verifyingTemplate, setVerifyingTemplate] = useState(false)
  const [loadingMetaTemplates, setLoadingMetaTemplates] = useState(false)
  const [validatingCsv, setValidatingCsv] = useState(false)
  // Unified CSV validation dialog state
  const [csvErrorDialogOpen, setCsvErrorDialogOpen] = useState(false)
  const [csvWarningDialogOpen, setCsvWarningDialogOpen] = useState(false)
  const [csvFileErrorDialogOpen, setCsvFileErrorDialogOpen] = useState(false)
  const [csvErrors, setCsvErrors] = useState<CsvErrorFlags>({})
  const [csvWarnings, setCsvWarnings] = useState<CsvWarningFlags>({})
  const [csvFileErrors, setCsvFileErrors] = useState<CsvFileClientErrorFlags>({})
  const [invalidCsvUrl, setInvalidCsvUrl] = useState<string | null>(null)
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false)

  // Form state
  const [formData, setFormData] = useState<CampaignWizardState>({
    step1: { name: '', description: '' },
    step2: { template_id: '', variable_mappings: [] },
    step3: { audience_type: 'upload', column_mappings: {} },
    step4: { timezone: 'IST', start_at: getDefaultStartDate(), end_at: getDefaultEndDate(getDefaultStartDate()), send_window_start: getRandomStartWindow(), send_window_end: '18:00' },
  })
  
  // Sync local CSV state with persisted formData.step3 on load/refresh
  useEffect(() => {
    if (formData.step3) {
      if (formData.step3.csv_columns && csvColumns.length === 0) {
        setCsvColumns(formData.step3.csv_columns)
      }
      if (formData.step3.csv_preview && csvPreview.length === 0) {
        setCsvPreview(formData.step3.csv_preview)
      }
      if (formData.step3.total_rows !== undefined && totalRows === 0) {
        setTotalRows(formData.step3.total_rows)
      }
    }
  }, [formData.step3])

  // Restore CSV File from IndexedDB on load/refresh
  useEffect(() => {
    const restoreFile = async () => {
      if (smbId && !csvFile) {
        const persistedFile = await filePersistence.loadFile(`${smbId}_campaign_csv`);
        if (persistedFile) {
          console.log('Restored CSV file from IndexedDB:', persistedFile.name);
          setCsvFile(persistedFile);
        }
      }
    };
    restoreFile();
  }, [smbId])

  // Derive selectedTemplate from formData to ensure persistence
  const selectedTemplate = formData.step2?.template_id
    ? templates.find(t => t.id === formData.step2?.template_id) || null
    : null

  // Load campaign data if in edit mode
  useEffect(() => {
    if (!isEditMode || !campaignId) {
      console.log('Edit mode check:', { isEditMode, campaignId })
      return
    }

    console.log('Loading campaign for edit mode:', campaignId)

    const loadCampaign = async () => {
      try {
        setLoading(true)
        console.log('Starting campaign load...')

        // Load campaign data
        const campaign = await campaignApi.getCampaign(campaignId)
        console.log('Campaign loaded:', campaign)
        setSmbId(campaign.smb_id)

        // Load campaign data into form
        const loadedFormData = {
          step1: {
            name: campaign.name,
            campaign_type: campaign.campaign_type,
            description: (campaign as any).description,
          },
          step2: {
            template_id: campaign.template_id || undefined,
            variable_mappings: (campaign as any).template_variable_mappings || [],
          },
          step3: {
            audience_type: (campaign as any).audience_filter?.audience_type ||
              ((campaign as any).leadbook ? 'upload' : 'upload'),
          },
          step4: {
            start_at: campaign.start_at ? new Date(campaign.start_at).toISOString().slice(0, 16) : undefined,
            end_at: (campaign as any).end_at ? new Date((campaign as any).end_at).toISOString().slice(0, 16) : undefined,
            timezone: campaign.timezone,
            send_window_start: (campaign as any).send_window_start || undefined,
            send_window_end: (campaign as any).send_window_end || undefined,
          },
        }
        console.log('Setting form data:', loadedFormData)
        setFormData(loadedFormData)

        // Load leadbook info if exists
        if ((campaign as any).leadbook) {
          const leadbook = (campaign as any).leadbook
          setExistingLeadbook({
            filename: leadbook.original_filename,
            uploaded_at: leadbook.uploaded_at,
          })
        }

        // Templates and variables will be loaded by the useEffect hooks
        // based on smbId and formData.step2.template_id
        console.log('Campaign edit mode loaded successfully')
      } catch (error) {
        console.error('Failed to load campaign:', error)
        showNotification('Failed to load campaign', 'error')
      } finally {
        setLoading(false)
      }
    }
    loadCampaign()
  }, [isEditMode, campaignId])

  // Initialize SMB and draft state from SMBContext
  useEffect(() => {
    if (isEditMode) return

    if (selectedSMBId && !smbId) {
      setSmbId(selectedSMBId)

      // Load saved state for this SMB
      const saved = campaignStorage.load(selectedSMBId)
      if (saved) {
        setFormData(saved)
      }
    }
  }, [isEditMode, selectedSMBId, smbId])

  // Load templates when SMB is selected
  useEffect(() => {
    if (smbId && currentStep >= 2) {
      loadTemplates()
      if (metaTemplates.length === 0) {
        loadMetaTemplates()
      }
    }
  }, [smbId, currentStep])

  // Sync template variables when template is selected (derived from formData)
  useEffect(() => {
    const syncTemplateVariables = async () => {
      if (selectedTemplate && selectedTemplate.id) {
        try {
          const response = await templatesApi.getTemplateVariables(selectedTemplate.id)
          console.log('Template variables synced:', response.variables)
          setTemplateVariables(response.variables || [])

          if (response.variables && response.variables.length > 0) {
            // Build new mappings but preserve existing ones if they are still valid
            const existingMappings = formData.step2?.variable_mappings || []
            const newVariables = response.variables || []

            const updatedMappings = newVariables.map((variable: any) => {
              const parameterName = extractTemplateParameterName(variable.parameter_name || variable.placeholder, variable.index)
              const label = formatTemplateVariableLabel(parameterName, variable.index)

              // Check if we already have a mapping for this parameter
              const existing = existingMappings.find(m => m.template_var === parameterName)

              if (existing) {
                return existing // Keep existing
              }

              // No default mapping here - mapping happens in Step 3
              return {
                csv_column: '',
                template_var: parameterName,
                user_label: label,
              }
            })

            handleStep2Change('variable_mappings', updatedMappings)
          }
        } catch (error) {
          console.error('Failed to sync template variables:', error)
        }
      } else {
        setTemplateVariables([])
      }
    }
    syncTemplateVariables()
  }, [selectedTemplate?.id])

  const loadTemplates = async () => {
    if (!smbId) {
      console.log('Cannot load templates: no smbId')
      return
    }
    try {
      console.log('Loading templates for SMB:', smbId)
      const response = await templatesApi.getTemplates({
        smb_id: smbId,
        limit: 100,
      })
      const templatesList = response.templates || []
      console.log('Templates loaded:', templatesList.length)
      setTemplates(templatesList)
    } catch (error) {
      console.error('Failed to load templates:', error)
      showNotification('Failed to load templates', 'error')
    }
  }

  const handleStep1Change = (field: string, value: any) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        step1: { ...prev.step1, [field]: value },
      }
      if (smbId) {
        campaignStorage.save(smbId, updated)
      }
      return updated
    })
  }

  const handleStep2Change = (field: string, value: any) => {
    console.log("Field and value", field, value)
    setFormData((prev) => {
      const updated = {
        ...prev,
        step2: { ...prev.step2, [field]: value },
      }
      if (smbId) {
        campaignStorage.save(smbId, updated)
      }
      return updated
    })
    setCsvErrors({})
    setCsvWarnings({})
  }

  const handleStep3Change = (field: string, value: any) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        step3: { ...prev.step3, [field]: value },
      }
      if (smbId) {
        campaignStorage.save(smbId, updated)
      }
      return updated
    })
    setCsvErrors({})
    setCsvWarnings({})
  }

  const handleStep4Change = (field: string, value: any) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        step4: { ...prev.step4, [field]: value },
      }
      if (smbId) {
        campaignStorage.save(smbId, updated)
      }
      return updated
    })
  }

  const handleTemplateSelect = async (templateId: string) => {
    console.log('handleTemplateSelect called with:', templateId)

    // Avoid redundant resets if the same template is selected again
    if (templateId && templateId === formData.step2?.template_id) {
      return
    }

    // Reset Step 3 whenever template is changed/cleared
    setCsvFile(null)
    setCsvPreview([])
    setCsvColumns([])
    setTotalRows(0)

    // Atomic update: Set new ID (or clear it) and reset mappings + Step 3
    setFormData((prev) => {
      const updated: CampaignWizardState = {
        ...prev,
        step2: { 
          ...prev.step2, 
          template_id: templateId || undefined, 
          variable_mappings: [] 
        },
        step3: {
          audience_type: 'upload',
          column_mappings: {},
          csv_filename: undefined,
          csv_columns: undefined,
          csv_preview: undefined,
          total_rows: undefined,
        }
      }
      
      if (smbId) {
        campaignStorage.save(smbId, updated)
        filePersistence.clearFile(`${smbId}_campaign_csv`)
      }
      
      return updated
    })

    if (templateId) {
      console.log('Template selected:', templateId)
    } else {
      console.log('Template selection cleared')
    }
  }


  const buildTemplateVariableMappings = (variables: Array<any>): VariableMapping[] => {
    return variables.map((variable) => {
      const templateVar = extractTemplateParameterName(variable.parameter_name || variable.placeholder, variable.index)
      const csvColumn = formatTemplateVariableLabel(templateVar, variable.index)

      return {
        csv_column: csvColumn,
        template_var: templateVar,
        user_label: csvColumn,
      }
    })
  }

  const loadMetaTemplates = async () => {
    if (!smbId) return
    if (metaTemplates.length > 0) return  // already loaded, skip
    try {
      setLoadingMetaTemplates(true)
      const response = await templatesApi.getMetaTemplates(smbId)
      setMetaTemplates(response.templates || [])
    } catch {
      showNotification('Failed to fetch Meta templates', 'error')
    } finally {
      setLoadingMetaTemplates(false)
    }
  }

  const handleCreateTemplate = async () => {
    if (!smbId) {
      showNotification('Please select an SMB first', 'error')
      return
    }

    // Validate: Either body_text OR template_inputs must be provided
    if (!newTemplateData.name) {
      showNotification('Template name is required', 'error')
      return
    }

    let templateInputs: Array<{ parameter_name: string, label: string, placeholder?: string }> = []

    if (newTemplateData.body_text) {
      // Scenario A: body_text is provided - auto-detect variables
      const variables = detectVariablesFromBodyText(newTemplateData.body_text)

      const existingMappings = buildTemplateVariableMappings(variables)

      templateInputs = variables.map((v) => {
        const parameterName = extractTemplateParameterName(v.parameter_name || v.placeholder, v.index)
        const mapping = existingMappings.find(m => m.template_var === parameterName)
        return {
          parameter_name: parameterName,
          label: mapping?.csv_column || formatTemplateVariableLabel(parameterName, v.index),
          placeholder: v.placeholder
        }
      })
    } else {
      showNotification('Body text is required to create a template for now', 'error')
      return
    }

    try {
      setLoading(true)

      const response = await templatesApi.createTemplate(smbId, {
        name: newTemplateData.name,
        language: newTemplateData.language,
        category: newTemplateData.category,
        body_text: newTemplateData.body_text || undefined,
        template_status: 'approved',
        header_type: newTemplateData.header_type,
        media_id: newTemplateData.media_id || undefined,
        template_inputs: templateInputs,
        platform_template_id: selectedMetaTemplate?.platform_template_id || undefined,
        components: selectedMetaTemplate?.components
      })

      // Reload templates list
      await loadTemplates()

      // Verify-sync the newly created template to update it in the database
      // Commented out - verify-sync should not be called on create template
      // if (selectedMetaTemplate?.platform_template_id) {
      //   try {
      //     console.log('Calling verify-sync for newly created template:', selectedMetaTemplate.platform_template_id)
      //     await templatesApi.verifyTemplateStatus(smbId, selectedMetaTemplate.platform_template_id)
      //     console.log('Verify-sync completed successfully')
      //     // Reload templates again after sync to get updated data
      //     await loadTemplates()
      //   } catch (error) {
      //     console.error('Failed to verify-sync template:', error)
      //   }
      // }

      // Select the newly created template
      handleTemplateSelect(response.id)

      // Close create template page and go back to select template page
      setCreateNewTemplate(false)

      // Reset form
      setSelectedMetaTemplate(null)
      setNewTemplateData({
        name: '',
        language: 'en_US',
        category: 'marketing',
        header_type: 'none',
        media_id: '',
        media_url: '',
        body_text: '',
      })
      setTemplateVariables([])

      showNotification('Template created successfully', 'success')
    } catch (error: any) {

      console.error('Failed to create template:', error)

      const backendError = error?.response?.data

      // extract message safely with fallback chain
      const errorMessage =
        backendError?.error?.message ||
        backendError?.detail ||
        error?.message ||
        'Failed to create template'

      // show notification
      showNotification(errorMessage, 'error')

    } finally {
      setLoading(false)
    }
  }

  // Removed handleColumnMapping - CSV column mapping happens automatically
  // based on matching template input labels with CSV column names

  const handleNext = () => {
    if (currentStep < 4) {
      setSearchParams({ step: (currentStep + 1).toString() })
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setSearchParams({ step: (currentStep - 1).toString() })
    } else {
      const hasChanges = formData.step1?.name || formData.step1?.description || csvFile || formData.step2?.template_id
        if (hasChanges) {
          setDiscardDialogOpen(true)
        }else{
          navigate('/campaigns')
        }
    }
  }

  const handleConfirmDiscard = () => {
    // Cleanup logic for the CURRENT smbId
    if (smbId) {
      campaignStorage.clear(smbId)
      filePersistence.clearFile(`${smbId}_campaign_csv`)
    }

    setDiscardDialogOpen(false)
    navigate('/campaigns')
  }

  const proceedToNextStep = () => {
    if (smbId) {
      campaignStorage.save(smbId, formData)
    }
    handleNext()
  }

  const handleSaveAndContinue = async () => {
    // Validate current step before allowing navigation
    if (currentStep === 1) {
      // Step 1: Validate SMB and name
      if (!smbId) {
        showNotification('Please select an SMB', 'error')
        return
      }
      if (!formData.step1?.name || formData.step1.name.trim() === '') {
        showNotification('Campaign name is required', 'error')
        return
      }
    } else if (currentStep === 2) {
      // Step 2: Validate template is selected
      if (!formData.step2?.template_id) {
        showNotification('Please select a template before continuing', 'error')
        return
      }

      const selectedTpl = templates.find(t => t.id === formData.step2?.template_id)
      if (selectedTpl && selectedTpl.platform_template_id && smbId) {
        try {
          setVerifyingTemplate(true)
          console.log("selectedTpl", selectedTpl)

          console.log("Calling verifyTemplateStatus")
          const response = await templatesApi.verifyTemplateStatus(smbId, [selectedTpl.platform_template_id])
          const tplResult = response[selectedTpl.platform_template_id]
          if (!tplResult?.can_proceed && tplResult?.status !== 'pending') {
            showNotification(tplResult?.message || 'Template is not approved', 'error')
            setVerifyingTemplate(false)
            return
          }
        } catch (error) {
          console.error('Failed to verify template status:', error)
          showNotification('Failed to verify template status', 'error')
          setVerifyingTemplate(false)
          return
        } finally {
          setVerifyingTemplate(false)
        }
      }
    } else if (currentStep === 3) {
      // Step 3: Validate audience
      const audienceType = formData.step3?.audience_type || 'upload'
      if (audienceType === 'upload') {
        if (!csvFile && !existingLeadbook) {
          showNotification('Please upload a CSV file or select Master Lead Book', 'error')
          return
        }
        // Validate column mappings if CSV is uploaded
        if (csvFile && !formData.step3?.column_mappings?.phone) {
          showNotification('Please map the Contact field in Lead Details section', 'error')
          return
        }

        // Check all template variables are mapped
        const unmappedVars = (formData.step2?.variable_mappings || []).filter(m => !m.csv_column);
        if (csvFile && unmappedVars.length > 0) {
          const unmappedLabels = unmappedVars.map(m => m.user_label).join(', ');
          showNotification(`Please map: "${unmappedLabels}" in Template Message Fields section`, 'error');
          return;
        }

        // Validate CSV for empty cells in required columns
        if (csvFile) {
          // 1. Client-side pre-validation (fast, zero network)
          const validation = await validateCsvFile(csvFile)
          if (validation.error) {
            setCsvFileErrors(validation.error)
            setInvalidCsvUrl(null)
            setCsvFileErrorDialogOpen(true)
            return
          }

          const requiredTemplateColumns: string[] = []
          const requiredLeadColumns: string[] = []
          const optionalNameColumn = formData.step3?.column_mappings?.name || ''
          if (formData.step3?.column_mappings?.phone) {
            requiredLeadColumns.push(formData.step3.column_mappings.phone)
          }
          (formData.step2?.variable_mappings || []).forEach(m => {
            if (m.csv_column && m.csv_column !== optionalNameColumn) requiredTemplateColumns.push(m.csv_column)
          })

          // 2. Backend validation
          try {
            setValidatingCsv(true)
            const result = await campaignApi.validateCsvColumns(
              smbId,
              csvFile,
              requiredLeadColumns,
              requiredTemplateColumns,
            )
            setValidatingCsv(false)

            setInvalidCsvUrl(result.invalid_csv_url ?? null)

            // Priority 1: blocking errors
            const hasErrors =
              result.has_duplicate_rows || result.missing_lead_data || result.invalid_phone_format
            if (hasErrors) {
              setCsvErrors({
                has_duplicate_rows: result.has_duplicate_rows,
                missing_lead_data: result.missing_lead_data,
                invalid_phone_format: result.invalid_phone_format,
              })
              setCsvErrorDialogOpen(true)
              return
            }

            // Priority 2: non-blocking warnings (only when 0 errors)
            const hasWarnings = result.has_duplicate_phone || result.missing_template_data
            if (hasWarnings) {
              setCsvWarnings({
                has_duplicate_phone: result.has_duplicate_phone,
                missing_template_data: result.missing_template_data,
              })
              setCsvWarningDialogOpen(true)
              return
            }
          } catch (error) {
            setValidatingCsv(false)
            resetCsvFileState()
            showNotification(
              'Unable to validate the CSV file. Please re-upload and try again.',
              'error',
            )
            return
          }
        }
      } else if (audienceType === 'master') {
        // Master lead book selected - no additional validation needed
        // Backend will validate that master leadbook exists when scheduling
      }
    }

    // Save state and proceed
    proceedToNextStep()
  }

  const handleDownloadInvalidCSV = () => {
    if (!invalidCsvUrl) return
    const link = document.createElement('a')
    link.href = invalidCsvUrl
    link.download = ''
    link.rel = 'noopener noreferrer'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  /** Clears local file states, step3 form data, and campaign/file storage. */
  const resetCsvFileState = () => {
    setCsvFile(null)
    setCsvColumns([])
    setCsvPreview([])
    setTotalRows(0)
    setInvalidCsvUrl(null)

    setFormData((prev) => {
      const updated: CampaignWizardState = {
        ...prev,
        step3: {
          ...prev.step3,
          csv_filename: undefined,
          csv_columns: undefined,
          csv_preview: undefined,
          total_rows: undefined,
          column_mappings: {},
        },
      }
      if (smbId) campaignStorage.save(smbId, updated)
      return updated
    })

    if (smbId) filePersistence.clearFile(`${smbId}_campaign_csv`)
  }

  /** Called from ErrorDialog "Go Back" — clears errors and resets file state. */
  const handleCsvErrorDialogClose = () => {
    setCsvErrorDialogOpen(false)
    setCsvErrors({})
    resetCsvFileState()
  }

  /** Called from FileErrorDialog "Go Back" — clears file errors and resets file state. */
  const handleCsvFileErrorDialogClose = () => {
    setCsvFileErrorDialogOpen(false)
    setCsvFileErrors({})
    resetCsvFileState()
  }

  /** Called from WarningDialog "Go Back" — keeps the file (user may fix column mappings). */
  const handleCsvWarningDialogClose = () => {
    setCsvWarningDialogOpen(false)
    setCsvWarnings({})
  }

  const handleSaveAsDraft = async () => {
    try {
      setLoading(true)

      // Validate SMB
      if (!smbId) {
        showNotification('Please select an SMB', 'error')
        setLoading(false)
        return
      }

      // Validate name
      if (!formData.step1?.name || formData.step1.name.trim() === '') {
        showNotification('Campaign name is required', 'error')
        setLoading(false)
        return
      }

      // Prepare variable mappings
      const variableMappings = formData.step2?.variable_mappings && formData.step2.variable_mappings.length > 0
        ? formData.step2.variable_mappings
        : []

      // Validate template is selected
      if (!formData.step2?.template_id) {
        showNotification('Please select a template before creating the campaign', 'error')
        setLoading(false)
        return
      }

      // Get audience type
      const audienceType = formData.step3?.audience_type || 'upload'

      // Validate mapping for upload audience
      if (audienceType === 'upload') {
        if (!csvFile && !existingLeadbook) {
          showNotification('Please upload a CSV file or select Master Lead Book', 'error')
          setLoading(false)
          return
        }

        // Check Lead phone mapping
        if (csvFile && !formData.step3?.column_mappings?.phone) {
          showNotification('Please map the Contact field in Lead Details section', 'error')
          setLoading(false)
          return
        }

        // Check all template variables are mapped
        const unmappedVars = (formData.step2?.variable_mappings || []).filter(m => !m.csv_column);
        if (csvFile && unmappedVars.length > 0) {
          const unmappedLabels = unmappedVars.map(m => m.user_label).join(', ');
          showNotification(`Please map: "${unmappedLabels}" in Template Message Fields section`, 'error');
          setLoading(false)
          return
        }
      }

      const createPayload: any = {
        name: formData.step1?.name || 'Untitled Campaign',
        campaign_type: formData.step1?.campaign_type || 'broadcast',
        description: formData.step1?.description,
        template_id: formData.step2.template_id, // Always include if validation passed
        variable_mappings: variableMappings,
        audience_type: audienceType, // Include audience type in create payload
        timezone: formData.step4?.timezone || 'IST', // Include timezone for draft
        start_at: formData.step4?.start_at || getDefaultStartDate(),
        end_at: formData.step4?.end_at || getDefaultEndDate(),
        send_window_start: formData.step4?.send_window_start || getRandomStartWindow(),
        send_window_end: formData.step4?.send_window_end || '18:00',
      }

      const campaign = await campaignApi.createCampaign(smbId, createPayload)

      // Upload audience if CSV is uploaded (for 'upload' audience type)
      // For 'master' audience type, no upload is needed - it will use master leadbook when scheduled
      if (audienceType === 'upload' && csvFile && formData.step3?.column_mappings) {
        await campaignApi.uploadAudience(
          campaign.id,
          csvFile,
          formData.step3.column_mappings
        )
      }

      if (smbId) {
        campaignStorage.clear(smbId)
        filePersistence.clearFile(`${smbId}_campaign_csv`)
      }

      const audienceMessage = audienceType === 'master'
        ? 'Campaign saved as draft. Master Lead Book will be used when scheduled.'
        : 'Campaign saved as draft'
      showNotification(audienceMessage, 'success')
      navigate(`/campaigns/${campaign.id}`)
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to save campaign', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleSchedule = async () => {
    try {
      setLoading(true)

      // Validate SMB
      if (!smbId) {
        showNotification('Please select an SMB', 'error')
        setLoading(false)
        return
      }

      // Validate name
      if (!formData.step1?.name || formData.step1.name.trim() === '') {
        showNotification('Campaign name is required', 'error')
        setLoading(false)
        return
      }

      // Validate template is selected
      if (!formData.step2?.template_id) {
        showNotification('Please select a template before scheduling the campaign', 'error')
        setLoading(false)
        return
      }

      // Validate schedule fields - use defaults if not set
      const validatedTimezone = formData.step4?.timezone || 'IST'
      const validatedStartAt = formData.step4?.start_at || getDefaultStartDate()

      if (!validatedStartAt) {
        showNotification('Start date is required', 'error')
        setLoading(false)
        return
      }

      if (!validatedTimezone) {
        showNotification('Timezone is required', 'error')
        setLoading(false)
        return
      }

      // Ensure defaults are set in formData if missing
      if (!formData.step4?.timezone) {
        handleStep4Change('timezone', validatedTimezone)
      }
      if (!formData.step4?.start_at) {
        handleStep4Change('start_at', validatedStartAt)
      }

      // Validate audience
      const audienceType = formData.step3?.audience_type || 'upload'
      if (audienceType === 'upload') {
        if (!csvFile && !isEditMode && !existingLeadbook) {
          showNotification('Please upload a CSV file or select Master Lead Book', 'error')
          setLoading(false)
          return
        }
        // Validate column mappings if CSV is uploaded
        if (csvFile && !formData.step3?.column_mappings?.phone) {
          showNotification('Please map the Contact field in Lead Details section', 'error')
          setLoading(false)
          return
        }

        // Check all template variables are mapped
        const unmappedVars = (formData.step2?.variable_mappings || []).filter(m => !m.csv_column);
        if (csvFile && unmappedVars.length > 0) {
          const unmappedLabels = unmappedVars.map(m => m.user_label).join(', ');
          showNotification(`Please map: "${unmappedLabels}" in Template Message Fields section`, 'error');
          setLoading(false)
          return
        }
      } else if (audienceType === 'master') {
        // Master lead book selected - backend will validate that master leadbook exists for this SMB
        // No additional validation needed here
      } else {
        showNotification('Please select a valid audience source', 'error')
        setLoading(false)
        return
      }

      let campaign
      if (isEditMode && campaignId) {
        // Update existing campaign
        campaign = await campaignApi.updateCampaign(campaignId, {
          name: formData.step1?.name,
          campaign_type: formData.step1?.campaign_type,
          description: formData.step1?.description,
        })
      } else {
        // Prepare variable mappings
        const variableMappings = formData.step2?.variable_mappings && formData.step2.variable_mappings.length > 0
          ? formData.step2.variable_mappings
          : []

        // Get audience type
        const audienceType = formData.step3?.audience_type || 'upload'

        // Create campaign with template
        const createPayload: any = {
          name: formData.step1?.name || 'Untitled Campaign',
          campaign_type: formData.step1?.campaign_type || 'broadcast',
          description: formData.step1?.description,
          template_id: formData.step2.template_id, // Always include if validation passed
          variable_mappings: variableMappings,
          audience_type: audienceType, // Include audience type in create payload
          timezone: formData.step4?.timezone || 'IST',
          start_at: formData.step4?.start_at || getDefaultStartDate(),
          end_at: formData.step4?.end_at || getDefaultEndDate(),
          send_window_start: formData.step4?.send_window_start || getRandomStartWindow(),
          send_window_end: formData.step4?.send_window_end || '18:00',
        }

        campaign = await campaignApi.createCampaign(smbId, createPayload)
      }

      // Attach template if not already attached (for edit mode)
      if (isEditMode && formData.step2?.template_id && formData.step2.template_id !== campaign.template_id) {
        const variableMappings = formData.step2.variable_mappings && formData.step2.variable_mappings.length > 0
          ? formData.step2.variable_mappings
          : []

        await campaignApi.attachTemplate(
          campaign.id,
          formData.step2.template_id,
          variableMappings
        )
      }

      // Upload audience (only if new CSV file is uploaded and audience_type is 'upload')
      // For 'master' audience type, the backend will use the master leadbook when scheduling
      if (audienceType === 'upload' && csvFile && formData.step3?.column_mappings) {
        await campaignApi.uploadAudience(
          campaign.id,
          csvFile,
          formData.step3.column_mappings
        )
      }

      // Schedule - include all parameters and audience_type
      // Use defaults if values are missing
      const scheduleTimezone = formData.step4?.timezone || 'IST'
      const scheduleStartAt = formData.step4?.start_at || getDefaultStartDate()
      const scheduleEndAt = formData.step4?.end_at || getDefaultEndDate()
      const scheduleWindowStart = formData.step4?.send_window_start || getRandomStartWindow()
      const scheduleWindowEnd = formData.step4?.send_window_end || '18:00'

      if (scheduleStartAt && scheduleTimezone) {
        const scheduleData: any = {
          start_at: scheduleStartAt.length === 16 ? `${scheduleStartAt}:00` : scheduleStartAt,
          timezone: scheduleTimezone,
          audience_type: formData.step3?.audience_type || 'upload', // Include audience type
        }

        // Always include end_at if we have a value (from formData or default)
        if (scheduleEndAt) {
          scheduleData.end_at = scheduleEndAt.length === 16 ? `${scheduleEndAt}:00` : scheduleEndAt
        }

        // Always include send_window_start and send_window_end
        scheduleData.send_window_start = scheduleWindowStart
        scheduleData.send_window_end = scheduleWindowEnd

        console.log('Scheduling campaign with data:', scheduleData)
        await campaignApi.scheduleCampaign(campaign.id, scheduleData)
      }

      if (smbId) {
        campaignStorage.clear(smbId)
        filePersistence.clearFile(`${smbId}_campaign_csv`)
      }

      showNotification('Campaign scheduled successfully', 'success')
      navigate(`/campaigns/${campaign.id}`)
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to schedule campaign', 'error')
    } finally {
      setLoading(false)
    }
  }

  const renderStep1 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Campaign Setup
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Provide basic campaign information
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
        <TextField
          label="Campaign Name"
          required
          fullWidth
          value={formData.step1?.name || ''}
          onChange={(e) => handleStep1Change('name', e.target.value)}
        />
        <TextField
          label="Description"
          multiline
          rows={3}
          fullWidth
          value={formData.step1?.description || ''}
          onChange={(e) => handleStep1Change('description', e.target.value)}
          placeholder="Describe the purpose of this campaign..."
        />
      </Box>
    </Box>
  )

  const renderStep2 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select or Add Message Template
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Templates are created on Meta. Add a template once and reuse it for future campaigns.
      </Typography>

      {!createNewTemplate && (() => {
        const availableMetaTemplates = metaTemplates.filter(t => !templates.some(dbT => dbT.platform_template_id === t.platform_template_id))
        const hasAvailableTemplates = availableMetaTemplates.length > 0
        const isLoading = loadingMetaTemplates
        const selectedSmb = smbs.find((s: any) => s.id === smbId)
        const metaCreateTemplateUrl = getMetaCreateTemplateUrl(
          selectedSmb?.whatsapp_business_portfolio_id,
          selectedSmb?.whatsapp_business_account_id
        )

        return (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                variant="outlined"
                component="a"
                href={metaCreateTemplateUrl}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon fontSize="small" />}
              >
                Create template on Meta
              </Button>
              <Tooltip 
                title={!hasAvailableTemplates && !isLoading ? "No new Meta templates" : ""} 
                arrow 
                enterTouchDelay={0}
              >
                <Box component="span" sx={{ display: 'inline-block' }}>
                  <Button
                    variant="outlined"
                    onClick={() => { setCreateNewTemplate(true); loadMetaTemplates() }}
                    disabled={!hasAvailableTemplates || isLoading}
                  >
                    {isLoading ? 'Loading...' : 'Add template from Meta'}
                  </Button>
                </Box>
              </Tooltip>
            </Box>
            {!hasAvailableTemplates && !isLoading && (
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}
              >
                No new Meta templates available
              </Typography>
            )}
          </Box>
        )
      })()}

      {!createNewTemplate ? (
        <Box>
          {/* Section A: Your Saved Templates */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
              Your Saved Templates
            </Typography>

            {templates.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <TemplateSelector 
                  smbId={smbId}
                  value={formData.step2?.template_id || ''}
                  label="Select a Template"
                  onSelect={(template) => {
                    handleTemplateSelect(template?.id || '')
                  }}
                />
              </Box>
            )}

            {templates.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No saved templates found. Create a template on Meta, then add it here to get started.
              </Alert>
            ) : (
              <>
                {/* <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Or browse templates below:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {templates.map((template) => (
                    <Card
                      key={template.id}
                      sx={{
                        border: formData.step2?.template_id === template.id ? 2 : 1,
                        borderColor: formData.step2?.template_id === template.id ? 'primary.main' : 'divider',
                        backgroundColor: formData.step2?.template_id === template.id ? 'rgba(25, 118, 210, 0.05)' : 'white',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 2
                        },
                        transition: 'all 0.2s',
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="h6" gutterBottom sx={{ flex: 1 }}>
                            {template.name}
                          </Typography>
                          {formData.step2?.template_id === template.id && (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Selected"
                              color="primary"
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                          <Typography variant="body2" color="textSecondary">
                            Channel: WhatsApp
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Language: {template.language}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Category: {template.category}
                          </Typography>
                          <Chip
                            label={template.template_status}
                            size="small"
                            color={
                              template.template_status === 'approved'
                                ? 'success'
                                : template.template_status === 'pending'
                                  ? 'warning'
                                  : 'default'
                            }
                            sx={{ alignSelf: 'flex-start', mt: 1 }}
                          />
                        </Box>
                        <Button
                          variant={formData.step2?.template_id === template.id ? 'contained' : 'outlined'}
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation()
                            handleTemplateSelect(template.id)
                          }}
                          sx={{ mt: 1 }}
                        >
                          {formData.step2?.template_id === template.id ? 'Selected' : 'Select Template'}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </Box> */}
              </>
            )}
          </Box>

          {/* Selected Template Display */}
          {selectedTemplate && formData.step2?.template_id && (
            <Alert
              severity="success"
              sx={{ mt: 3, mb: 2 }}
              icon={<CheckCircleIcon />}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Selected Template: {selectedTemplate.name}
              </Typography>
              {templateVariables.length > 0 && (
                <Typography variant="body2">
                  {templateVariables.length} variable{templateVariables.length > 1 ? 's' : ''} detected
                </Typography>
              )}
            </Alert>
          )}

          {/* Existing Template Preview moved to right panel */}


        </Box>
      ) : (
        <>
          <Typography variant="subtitle1" gutterBottom>
            Add Template from Meta
          </Typography>
          <Alert severity="info" sx={{ mb: 4 }}>
            Pick a template you've created and approved in Meta Business Manager.
          </Alert>

          <Box sx={{ mb: 3 }}>
            <TemplateSelector 
              type="meta"
              enableFilters={false}
              key={createNewTemplate ? 'meta-template-create' : 'meta-template-select'}
              smbId={smbId}
              value={selectedMetaTemplate?.platform_template_id || ''}
              unavailableTemplateIds={templates.map(t => t.platform_template_id).filter(Boolean) as string[]}
              onSelect={(tpl) => {
                setSelectedMetaTemplate(tpl as MetaTemplate)
                if (tpl) {
                  const c = getComponentConstraints(tpl)
                  setNewTemplateData({
                    ...newTemplateData,
                    name: tpl.name,
                    language: tpl.language,
                    category: tpl.category.toLowerCase(),
                    body_text: c.bodyText,
                    header_type: c.headerFormat || 'none',
                    media_id: '',
                    media_url: c.headerContent || '',
                  })
                  if (c.bodyText) {
                    const vars = detectVariablesFromBodyText(c.bodyText)
                    setTemplateVariables(vars)
                    handleStep2Change('variable_mappings', buildTemplateVariableMappings(vars))
                  }
                } else {
                  setNewTemplateData({ name: '', language: 'en_US', category: 'marketing', header_type: 'none', media_id: '', media_url: '', body_text: '' })
                  setTemplateVariables([])
                  handleStep2Change('variable_mappings', [])
                }
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {(() => {
              // const c = getComponentConstraints(selectedMetaTemplate)
              return (
                <>
                  {/* <br /> */}
                  {/* Language Field */}
                  {/* <TextField
                    label="Language"
                    required
                    fullWidth
                    helperText="e.g. en_US"
                    value={newTemplateData.language}
                    disabled={!!selectedMetaTemplate}
                    onChange={(e) => setNewTemplateData({ ...newTemplateData, language: e.target.value })}
                  /> */}
                  {/* Category Field */}
                  {/* <FormControl fullWidth required disabled={!!selectedMetaTemplate}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={newTemplateData.category}
                      label="Category"
                      onChange={(e) => setNewTemplateData({ ...newTemplateData, category: e.target.value })}
                    >
                      <MenuItem value="marketing">Marketing</MenuItem>
                      <MenuItem value="utility">Utility</MenuItem>
                      <MenuItem value="authentication">Authentication</MenuItem>
                    </Select>
                  </FormControl> */}
                  {/* Header Type Field */}
                  {/* {(!selectedMetaTemplate || c.hasHeader) && (
                    <FormControl fullWidth disabled={!!selectedMetaTemplate}>
                      <InputLabel>Header Type</InputLabel>
                      <Select
                        value={newTemplateData.header_type}
                        label="Header Type"
                        onChange={(e) => setNewTemplateData({ ...newTemplateData, header_type: e.target.value })}
                      >
                        <MenuItem value="none">None</MenuItem>
                        <MenuItem value="text">Text</MenuItem>
                        <MenuItem value="image">Image</MenuItem>
                        <MenuItem value="video">Video</MenuItem>
                        <MenuItem value="document">Document</MenuItem>
                        <MenuItem value="audio">Audio (Coming Soon)</MenuItem>
                        <MenuItem value="sticker">Sticker (Coming Soon)</MenuItem>
                      </Select>
                    </FormControl>
                  )} */}
                  {/* Upload Media Section */}
                  {/* {(!selectedMetaTemplate || c.hasHeader) && ['image', 'video', 'document', 'audio', 'sticker'].includes(newTemplateData.header_type) && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Upload Media
                      </Typography>
                      <MediaUpload
                        smbId={smbId}
                        headerType={newTemplateData.header_type as 'image' | 'video' | 'document' | 'audio' | 'sticker'}
                        onUploadComplete={(mediaId, thumbnailUrl, originalUrl) => {
                          const url = originalUrl || thumbnailUrl || '';
                          setNewTemplateData(prev => ({
                            ...prev,
                            media_id: mediaId,
                            media_url: url
                          }))
                        }}
                        onRemove={() => {
                          setNewTemplateData(prev => ({ ...prev, media_id: '', media_url: '' }))
                        }}
                        currentMediaId={newTemplateData.media_id || undefined}
                      />
                    </Box>
                  )} */}
                  {/* Body Text Field */}
                  {/* {(!selectedMetaTemplate || c.hasBody) && (
                    <TextField
                      label="Body Text"
                      fullWidth
                      multiline
                      rows={4}
                      helperText={selectedMetaTemplate ? 'Body text from Meta template (read-only)' : 'Required for now. Use {{1}}, {{2}}, or {{customer_name}} for variables.'}
                      value={newTemplateData.body_text}
                      InputProps={{ readOnly: !!selectedMetaTemplate }}
                      onChange={(e) => {
                        if (selectedMetaTemplate) return
                        setNewTemplateData({ ...newTemplateData, body_text: e.target.value })
                        if (e.target.value) {
                          const variables = detectVariablesFromBodyText(e.target.value)
                          setTemplateVariables(variables)
                          handleStep2Change('variable_mappings', buildTemplateVariableMappings(variables))
                        } else {
                          setTemplateVariables([])
                          handleStep2Change('variable_mappings', [])
                        }
                      }}
                    />
                  )} */}
                  {/* Buttons Section */}
                  {/* {selectedMetaTemplate && c.hasButtons && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Buttons (from Meta template)</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {c.buttons.map((btn: any, i: number) => (
                          <Chip key={i} label={btn.text || btn.type} variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                  )} */}
                </>
              )
            })()}

            {/* Preview for New Template moved to right panel */}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setCreateNewTemplate(false)
                  setSelectedMetaTemplate(null)
                  setNewTemplateData({
                    name: '',
                    language: 'en_US',
                    category: 'marketing',
                    header_type: 'none',
                    media_id: '',
                    media_url: '',
                    body_text: '',
                  })
                  setTemplateVariables([])
                  handleStep2Change('variable_mappings', [])
                  handleStep2Change('template_id', undefined)
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateTemplate}
                disabled={!newTemplateData.name || loading || !newTemplateData.body_text}
              >
                Create Template
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  )

  const renderStep3 = () => {
    // Ensure audience_type is initialized
    const audienceType = formData.step3?.audience_type || 'upload'

    // Mutual exclusion logic
    const selectedInLead = Object.values(formData.step3?.column_mappings || {}) as string[];
    const selectedInTemplate = (formData.step2?.variable_mappings || []).map(m => m.csv_column);
    const allUsedColumns = [...selectedInLead, ...selectedInTemplate]
      .filter(Boolean)
      .filter(col => csvColumns.includes(col)); // Only include columns present in current file

    const templateColumnDetails = templateVariables.map((variable) => {
      const parameterName = extractTemplateParameterName((variable as any).parameter_name || variable.placeholder, variable.index)

      return {
        key: variable.index,
        variable: variable.placeholder,
        columnName: formatTemplateVariableLabel(parameterName, variable.index),
      }
    })

    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
            Select & Map Your Audience
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Upload your contact list and map columns to lead details and template message fields for personalized messaging.
          </Typography>
        </Box>
        <SampleCsvSection
          template_name={selectedTemplate?.name}
          templateVariables={templateVariables}
          components={selectedTemplate?.components}
        />
        <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
          Audience Source
        </Typography>
        <Box sx={{ mt: 0 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Select Audience Source</Typography>
            <RadioGroup
              sx={{ gap: 0, mt: 1 }}
              value={audienceType}
              onChange={(e) => handleStep3Change('audience_type', e.target.value)}
            >
              <FormControlLabel value="upload" control={<Radio size="medium" />} label={<Typography variant="body2">Upload CSV</Typography>} sx={{ my: -0.5, }} />
              <FormControlLabel value="master" control={<Radio size="medium" />} label={<Typography variant="body2">Use Master Lead Book</Typography>} sx={{ my: -0.5 }} />
            </RadioGroup>
          </FormControl>

          {audienceType === 'upload' && (
            <Box>
              {existingLeadbook && !csvFile && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Current leadbook: {existingLeadbook.filename || 'uploaded file'}
                  {existingLeadbook.uploaded_at && ` (uploaded ${new Date(existingLeadbook.uploaded_at).toLocaleDateString()})`}
                </Alert>
              )}
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                sx={{ mb: 1 }}
              >
                {existingLeadbook ? 'Replace CSV File' : 'Upload CSV File'}
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  onChange={handleCsvUpload}
                  onClick={(event) => { (event.target as HTMLInputElement).value = ''; }}
                />
              </Button>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
                CSV format only, up to 100 MB
              </Typography>
              {/* This Table is hidden for now. We can use it later when needed.  */}
              <Box sx={{ mt: 1, mb: 3, display: 'none' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Template Column Details
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Use these exact column names in your CSV for template variables.
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Variable</TableCell>
                        <TableCell>CSV Column Name</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {templateColumnDetails.length > 0 ? (
                        templateColumnDetails.map((detail) => (
                          <TableRow key={detail.key}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {detail.variable}
                              </Typography>
                            </TableCell>
                            <TableCell>{detail.columnName}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                              No template variables found for the selected template.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {(csvFile || formData.step3?.csv_filename) && (
                <Alert
                  severity={csvFile ? "success" : "info"}
                  sx={{ mb: 3 }}
                  icon={csvFile ? <CheckCircleIcon /> : <UploadIcon />}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {csvFile ? `✓ ${csvFile.name} uploaded successfully` : `Selected file: ${formData.step3?.csv_filename}`}
                  </Typography>
                  {totalRows > 0 && (
                    <Typography variant="body2" color="textSecondary">
                      {totalRows.toLocaleString()} record{totalRows > 1 ? 's' : ''} detected in file
                      {existingLeadbook && ' (will replace existing file)'}
                    </Typography>
                  )}
                </Alert>
              )}

              {csvPreview.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    CSV Preview:
                    <Box component='span' sx={{ fontStyle: 'italic' }}> {csvFile?.name} </Box>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}> First 5 Rows </Typography>
                  <TableContainer component={Paper} sx={{ border: 0.1, borderColor: 'text.secondary', boxShadow: 'none' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {csvColumns.map((col) => (
                            <TableCell key={col}>{col}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {csvPreview.map((row, idx) => (
                          <TableRow key={idx}>
                            {csvColumns.map((col) => (
                              <TableCell key={col}>{row[col] || ''}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Mapping Section Header */}
              {csvColumns.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    Map CSV Columns
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Align your uploaded CSV headers with the required lead details and template message fields below.
                  </Typography>
                </Box>
              )}

              {/* Column mappings for name and phone */}
              {csvColumns.length > 0 && (
                <Box sx={{ mt: 2.5 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Lead Details
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, mt: 2 }}>
                    <FormControl fullWidth required>
                      <InputLabel>Contact</InputLabel>
                      <Select
                        value={formData.step3?.column_mappings?.phone || ''}
                        label="Contact"
                        onChange={(e) => {
                          const mappings = { ...formData.step3?.column_mappings, phone: e.target.value }
                          handleStep3Change('column_mappings', mappings)
                        }}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {csvColumns.map((col) => (
                          <MenuItem
                            key={col}
                            value={col}
                            disabled={allUsedColumns.includes(col) && formData.step3?.column_mappings?.phone !== col}
                          >
                            {col}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel>Full Name (Optional)</InputLabel>
                      <Select
                        value={formData.step3?.column_mappings?.name || ''}
                        label="Full Name (Optional)"
                        onChange={(e) => {
                          const mappings = { ...formData.step3?.column_mappings, name: e.target.value }
                          handleStep3Change('column_mappings', mappings)
                        }}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {csvColumns.map((col) => (
                          <MenuItem
                            key={col}
                            value={col}
                            disabled={allUsedColumns.includes(col) && formData.step3?.column_mappings?.name !== col}
                          >
                            {col}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              )}

              {/* Template Message Fields mapping for variables */}
              {csvColumns.length > 0 && templateVariables.length > 0 && (
                <Box sx={{ mt: 2.5 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Template Message Fields
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 2 }}>
                    {templateVariables.map((variable) => {
                      const parameterName = extractTemplateParameterName((variable as any).parameter_name || variable.placeholder, variable.index);
                      const label = formatTemplateVariableLabel(parameterName, variable.index);
                      const currentMapping = formData.step2?.variable_mappings?.find(m => m.template_var === parameterName);

                      return (
                        <FormControl key={parameterName} fullWidth required>
                          <InputLabel>{label}</InputLabel>
                          <Select
                            value={currentMapping?.csv_column || ''}
                            label={label}
                            onChange={(e) => {
                              const newMappings = [...(formData.step2?.variable_mappings || [])];
                              const mappingIndex = newMappings.findIndex(m => m.template_var === parameterName);
                              const mappingValue = {
                                csv_column: e.target.value as string,
                                template_var: parameterName,
                                user_label: label
                              };
                              if (mappingIndex >= 0) {
                                newMappings[mappingIndex] = mappingValue;
                              } else {
                                newMappings.push(mappingValue);
                              }
                              handleStep2Change('variable_mappings', newMappings);
                            }}
                          >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {csvColumns.map((col) => (
                              <MenuItem
                                key={col}
                                value={col}
                                disabled={allUsedColumns.includes(col) && currentMapping?.csv_column !== col}
                              >
                                {col}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {audienceType === 'master' && (
            <Alert severity="info">
              Using Master Lead Book for this campaign. All leads associated with the selected SMB will be included.
            </Alert>
          )}
        </Box>
      </Box>
    )
  }

  // Initialize default values for Step 4 when step is accessed and smbId is loaded
  useEffect(() => {
    if (currentStep === 4 && smbId) {
      const start_at = getDefaultStartDate()
      const defaults = {
        timezone: 'IST',
        start_at,
        end_at: getDefaultEndDate(start_at),
        send_window_start: getRandomStartWindow(),
        send_window_end: '18:00',
      }

      // Only initialize if values are missing
      if (!formData.step4?.timezone) {
        handleStep4Change('timezone', defaults.timezone)
      }
      if (!formData.step4?.start_at) {
        handleStep4Change('start_at', defaults.start_at)
      }
      if (!formData.step4?.end_at) {
        handleStep4Change('end_at', defaults.end_at)
      }
      if (!formData.step4?.send_window_start) {
        handleStep4Change('send_window_start', defaults.send_window_start)
      }
      if (!formData.step4?.send_window_end) {
        handleStep4Change('send_window_end', defaults.send_window_end)
      }
    }
  }, [currentStep, smbId])

  const renderStep4 = () => (
    <Box>
      <Grid container spacing={4}>
        {/* Left Side: Input Fields */}
        <Grid item xs={12} md={7}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            Schedule & Launch
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TimezoneSelector
              value={formData.step4?.timezone || ''}
              onChange={(value: string) => handleStep4Change('timezone', value)}
            />
            
            <TextField
              label="Start Date & Time"
              type="datetime-local"
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.step4?.start_at || ''}
              onChange={(e) => handleStep4Change('start_at', e.target.value)}
            />
            
            <TextField
              label="End Date & Time"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.step4?.end_at || ''}
              onChange={(e) => handleStep4Change('end_at', e.target.value)}
            />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Send Window Start"
                  type="time"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={formData.step4?.send_window_start || ''}
                  onChange={(e) => handleStep4Change('send_window_start', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Send Window End"
                  type="time"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={formData.step4?.send_window_end || ''}
                  onChange={(e) => handleStep4Change('send_window_end', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Right Side: Template Preview */}
        <Grid item xs={12} md={5}> 
          {selectedTemplate && (
            <TemplatePreview 
              template={selectedTemplate} 
              containerSx={{ top: 0 }} // Remove sticky offset for better alignment in this grid
            />
          )}
        </Grid>
      </Grid>
    </Box>
  )

  const handleCsvUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // 1. Fast pre-validation (file size, extension, row count, column count, empty file)
    const validation = await validateCsvFile(file)
    if (validation.error) {
      resetCsvFileState()
      setCsvFileErrors(validation.error)
      setCsvFileErrorDialogOpen(true)
      return
    }

    try {
      const { headers, totalRows: total, lines } = validation

      // Valid CSV file — set state and build preview from top lines
      setCsvFile(file)
      if (smbId) {
        filePersistence.saveFile(`${smbId}_campaign_csv`, file)
      }
      setCsvErrors({})
      setCsvWarnings({})
      handleStep3Change('csv_file', file)
      handleStep3Change('audience_type', 'upload')

      setCsvColumns(headers)
      setTotalRows(total)

      // Build 5 preview rows from headerSlice lines
      const previewRows = lines.slice(1, 6).map((line) => {
        const values = line.split(',').map((v) => v.trim().replace(/^["']|["']$/g, ''))
        const row: Record<string, string> = {}
        headers.forEach((header, index) => {
          row[header] = values[index] || ''
        })
        return row
      })
      setCsvPreview(previewRows)

      // Auto-initialize column mappings for common column names
      const existingMappings = formData.step3?.column_mappings || {}
      const validMappings: Record<string, string> = {}
      Object.entries(existingMappings).forEach(([key, val]) => {
        if (headers.includes(val)) {
          validMappings[key] = val
        }
      })

      const autoMappings: Record<string, string> = { ...validMappings }
      const existingVariableMappings = formData.step2?.variable_mappings || []
      const updatedVariableMappings = existingVariableMappings.map((m) => {
        if (headers.includes(m.csv_column)) {
          return m
        }
        return { ...m, csv_column: '' }
      })

      const usedHeaders = new Set<string>()
      Object.values(autoMappings).forEach((val) => usedHeaders.add(val))
      updatedVariableMappings.forEach((m) => {
        if (m.csv_column) usedHeaders.add(m.csv_column)
      })

      if (!autoMappings.phone) {
        const phoneMatch = headers.find(
          (h) =>
            !usedHeaders.has(h) &&
            (h.toLowerCase().includes('phone') ||
              h.toLowerCase().includes('mobile') ||
              h.toLowerCase().includes('number') ||
              h.toLowerCase().includes('contact') ||
              h.toLowerCase() === 'phone'),
        )
        if (phoneMatch) {
          autoMappings.phone = phoneMatch
          usedHeaders.add(phoneMatch)
        }
      }

      if (!autoMappings.name) {
        const nameMatch = headers.find(
          (h) =>
            !usedHeaders.has(h) &&
            (h.toLowerCase().includes('name') ||
              h.toLowerCase().includes('full') ||
              h.toLowerCase() === 'name'),
        )
        if (nameMatch) {
          autoMappings.name = nameMatch
          usedHeaders.add(nameMatch)
        }
      }

      // Auto-initialize template variable mappings
      templateVariables.forEach((variable) => {
        const parameterName = extractTemplateParameterName(
          (variable as any).parameter_name || variable.placeholder,
          variable.index,
        )
        const label = formatTemplateVariableLabel(parameterName, variable.index)

        // Only auto-map if current mapping is empty or invalid
        const existingIdx = updatedVariableMappings.findIndex(
          (m) => m.template_var === parameterName,
        )
        if (existingIdx === -1 || !updatedVariableMappings[existingIdx].csv_column) {
          const match = headers.find(
            (h) =>
              !usedHeaders.has(h) &&
              (h.toLowerCase() === label.toLowerCase() ||
                h.toLowerCase().replace(/\s+/g, '_') === parameterName.toLowerCase() ||
                h.toLowerCase().includes(label.toLowerCase()) ||
                label.toLowerCase().includes(h.toLowerCase())),
          )

          if (match) {
            const mappingValue = { csv_column: match, template_var: parameterName, user_label: label }
            usedHeaders.add(match)
            if (existingIdx >= 0) {
              updatedVariableMappings[existingIdx] = mappingValue
            } else {
              updatedVariableMappings.push(mappingValue)
            }
          }
        }
      })

      setFormData((prev) => {
        const updatedState: CampaignWizardState = {
          ...prev,
          step2: {
            ...prev.step2,
            variable_mappings: updatedVariableMappings,
          },
          step3: {
            ...prev.step3,
            csv_filename: file.name,
            csv_columns: headers,
            csv_preview: previewRows,
            total_rows: total,
            column_mappings: autoMappings,
          },
        }
        if (smbId) campaignStorage.save(smbId, updatedState)
        return updatedState
      })
    } catch (error) {
      console.error('Failed to parse CSV:', error)
      showNotification('Failed to parse CSV file. Please check the file format.', 'error')
      handleCsvErrorDialogClose()
    }
  }

  const renderPreviewPanel = () => {
    if (currentStep !== 2) return null;

    let previewTpl: LocalTemplate | null = null;

    if (!createNewTemplate) {
      if (selectedTemplate && formData.step2?.template_id && smbId) {
        previewTpl = selectedTemplate;
      }
    } else {
      previewTpl = selectedMetaTemplate as unknown as LocalTemplate;
    }

    return (
      <TemplatePreview
        template={previewTpl}
      />
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 1.5, md: 3 }, pb: { xs: 10, md: 3 } }}>
      <Typography variant="h4" gutterBottom>
        {isEditMode ? `Edit Campaign${campaignId ? ` #${campaignId.slice(0, 8)}` : ''}` : 'Create Campaign'}
      </Typography>

      {isEditMode && loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Loading campaign data...
          </Typography>
        </Box>
      )}

      <Stepper 
        activeStep={currentStep - 1} 
        orientation={isMobile ? 'vertical' : 'horizontal'}
        sx={{ mb: 4 }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {currentStep === 2 ? (
        /* Step 2: Fixed two-column layout on desktop, stacked on mobile */
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          gap: 3, 
          alignItems: 'flex-start' 
        }}>
          {/* Form column */}
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '0 0 60%', 
            width: isMobile ? '100%' : '60%', 
            minWidth: 0, 
            overflowX: 'hidden' 
          }}>
            <Card sx={{ height: isMobile ? 'auto' : '640px', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box' }}>
                {renderStep2()}
              </CardContent>
            </Card>
            {!createNewTemplate && (
              isMobile ? (
                /* Mobile */
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Button
                    endIcon={verifyingTemplate ? <CircularProgress color="inherit" size={18} sx={{ mr: 1, mb:0.2 }} /> : <ArrowForwardIcon />}
                    variant="contained"
                    onClick={handleSaveAndContinue}
                    disabled={loading || verifyingTemplate}
                    fullWidth
                  >
                    Save & Continue
                  </Button>
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
                    <Button
                      startIcon={<ArrowBackIcon />}
                      onClick={handleBack}
                      disabled={loading}
                      sx={{ flex: 1 }}
                    >
                      Back
                    </Button>
                    {/* <Button
                      startIcon={<SaveIcon />}
                      variant="outlined"
                      onClick={handleSaveAsDraft}
                      disabled={loading}
                      sx={{ flex: 1 }}
                    >
                      Save as Draft
                    </Button> */}
                  </Box>
                </Box>
              ) : (
                /* Desktop */
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: 3, gap: 2 }}>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    disabled={loading}
                  >
                    Back
                  </Button>
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                    {/* <Button
                      startIcon={<SaveIcon />}
                      variant="outlined"
                      onClick={handleSaveAsDraft}
                      disabled={loading}
                    >
                      Save as Draft
                    </Button> */}
                    <Button
                      endIcon={verifyingTemplate ? <CircularProgress color="inherit" size={18} sx={{ mr: 1, mb:0.2 }} /> : <ArrowForwardIcon />}
                      variant="contained"
                      onClick={handleSaveAndContinue}
                      disabled={loading || verifyingTemplate}
                    >
                      Save & Continue
                    </Button>
                     
                  </Box>
                </Box>
              )
            )}
          </Box>

          {/* Preview column */}
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '0 0 40%', 
            width: isMobile ? '100%' : '40%', 
            minWidth: 0 
          }}>
            {renderPreviewPanel()}
          </Box>
        </Box>
      ) : (
        /* All other steps: single full-width column */
        <Box>
          <Card>
            <CardContent>
              {currentStep === 1 && renderStep1()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}
            </CardContent>
          </Card>
          {isMobile ? (
            /* Mobile: primary action on top, Back + Save as Draft below */
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {currentStep < 4 ? (
                <Button
                  endIcon={<ArrowForwardIcon />}
                  variant="contained"
                  onClick={handleSaveAndContinue}
                  disabled={loading || verifyingTemplate}
                  fullWidth
                >
                  Save & Continue
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSchedule}
                  disabled={loading}
                  fullWidth
                >
                  Schedule Campaign
                </Button>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                  disabled={loading}
                  sx={{ flex: 1 }}
                >
                  Back
                </Button>
                {/* <Button
                  startIcon={<SaveIcon />}
                  variant="outlined"
                  onClick={handleSaveAsDraft}
                  disabled={loading}
                  sx={{ flex: 1 }}
                >
                  Save as Draft
                </Button> */}
              </Box>
            </Box>
          ) : (
            /* Desktop: original layout — Back left, Save as Draft + primary action right */
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: 3, gap: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                disabled={loading}
              >
                Back
              </Button>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                <Button
                  startIcon={<SaveIcon />}
                  variant="outlined"
                  onClick={handleSaveAsDraft}
                  disabled={loading}
                  style={{display: currentStep != 4 ? 'none' : ''}}
                >
                  Save as Draft
                </Button>
                {currentStep < 4 ? (
                  <Button
                    endIcon={!validatingCsv ? <ArrowForwardIcon /> : undefined}
                  startIcon={validatingCsv ? <CircularProgress size={18} color="inherit" /> : undefined}
                    variant="contained"
                    onClick={handleSaveAndContinue}
                    disabled={loading || verifyingTemplate || validatingCsv}
                  >
                    {validatingCsv ? 'Validating...' : 'Save & Continue'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSchedule}
                    disabled={loading}
                  >
                    Schedule Campaign
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* CSV Validation — Error Dialog (blocking) */}
      <CsvValidationErrorDialog
        open={csvErrorDialogOpen}
        onClose={handleCsvErrorDialogClose}
        onDownloadReport={handleDownloadInvalidCSV}
        invalidCsvUrl={invalidCsvUrl}
        errors={csvErrors}
        phoneColumnName={formData.step3?.column_mappings?.phone || 'Contact'}
      />

      {/* CSV Validation — File Error Dialog (upload limit issues) */}
      <CsvFileClientErrorDialog
        open={csvFileErrorDialogOpen}
        onClose={handleCsvFileErrorDialogClose}
        errors={csvFileErrors}
      />

      {/* CSV Validation — Warning Dialog (non-blocking) */}
      <CsvValidationWarningDialog
        open={csvWarningDialogOpen}
        onClose={handleCsvWarningDialogClose}
        onDownloadReport={handleDownloadInvalidCSV}
        onContinueAnyway={() => { setCsvWarningDialogOpen(false); proceedToNextStep() }}
        invalidCsvUrl={invalidCsvUrl}
        warnings={csvWarnings}
      />

      {/* CSV Validation — Animated Loading Dialog */}
      <ValidateCsvLoadingDialog open={validatingCsv} />



      {/* Discard Changes Confirmation Dialog */}
      <Dialog 
        open={discardDialogOpen} 
        onClose={() => setDiscardDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Discard Changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to exit? All progress and uploaded data for this campaign will be cleared.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={() => setDiscardDialogOpen(false)} 
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDiscard} 
            variant="contained" 
            color="error"
            autoFocus
          >
            Discard & Exit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CampaignWizard
